import React, { useEffect, useState } from "react";
import DivTag from "../../Components/DivComponent/DivTag";
import ReportHeading from "../../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../../components/SeoComponent/Seo";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import {
  FetchAllServices,
  bulkAssignServices,
  FetchSearchServices,
} from "../../../services/PackageList/PackageService";
import Loader from "../../../components/Loader/Loader";
import { GetUserDataByIdService } from "../../../services/apiService";
import { User2Icon } from "lucide-react";

const ActivateServiceBulk = () => {
  const [userSearch, setUserSearch] = useState("");
  const [userDropdown, setUserDropdown] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // Filter state
  const [basicUserData, setBasicUserData] = useState(null);
  const [resultMessages, setResultMessages] = useState({
    success: [],
    errors: [],
  });
  const [searchData, setSearchData] = useState({
    searchKeyword: "",
    mobile: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [assignedDetails, setAssignedDetails] = useState([]);

  // Filter logic based on selected status
  const filteredAssignedDetails = assignedDetails.filter((api) => {
    if (statusFilter === "all") return true;
    return api.service_status === statusFilter;
  });

  const handleUserSearch = async (term, pageNum = 1, append = false) => {
    try {
      setIsUserLoading(true);
      setSearchTerm(term);

      const res = await FetchSearchServices({ search: term, page: pageNum });

      // ✅ Correct extraction based on your real structure
      const list = Array.isArray(res?.data) ? res.data : [];
      const pagination = res?.pagination;

      if (res?.status?.code === 200 && list.length > 0) {
        setUserDropdown((prev) => (append ? [...prev, ...list] : list));
        setShowUserDropdown(true);
        setPage(pagination?.current_page || 1);
        setHasMore(!!pagination?.next_page_url);
        // console.log("pagggg", page, pagination?.next_page_url, showUserDropdown);
      } else {
        setUserDropdown([]);
        setShowUserDropdown(false);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Search dropdown error:", error);
      setUserDropdown([]);
      setShowUserDropdown(false);
    } finally {
      setIsUserLoading(false);
    }
  };

  //  Debounce for typing (wait before searching)
  useEffect(() => {
    if (!userSearch.trim()) {
      setShowUserDropdown(false);
      setUserDropdown([]);
      return;
    }
    const timer = setTimeout(() => {
      handleUserSearch(userSearch, 1, false);
    }, 500);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const handleSelectUser = async (user) => {
    setUserSearch(user.user_id || "");
    setShowUserDropdown(false);

    await fetchAssignedDetails({
      userId: user.user_id,
      search: "",
    });
  };

  const getBasicUserDetails = async () => {
    try {
      const response = await GetUserDataByIdService({ userId: searchText });

      if (response) {
        setBasicUserData(response);
      }
    } catch (error) {
      console.log(error);
    }
  };

  //  Auto-fetch on full 8-digit User ID
  useEffect(() => {
    if (searchText.trim().length === 8) {
      getBasicUserDetails();
      fetchAssignedDetails({
        userId: searchText.trim().toUpperCase(),
        search: searchData.searchKeyword,
      });
    } else if (searchText.trim().length === 0) {
      setAssignedDetails([]);
    }
  }, [searchText]);

  const fetchAssignedDetails = async ({
    search = "",
    userId = "",
    mobileNumber = "",
    email = "",
  }) => {
    try {
      setIsLoading(true);

      const response = await FetchAllServices({
        search,
        userId,
        mobileNumber,
        email,
      });

      if (
        response?.status?.code === 200 &&
        Array.isArray(response?.data) &&
        response.data.length > 0
      ) {
        showSuccessToast(response?.message || "Data fetched successfully!");

        const enriched = response.data.map((item) => {
          const assigned = item.assignedServicePackages?.[0] || {};

          return {
            ...item,
            selected: false,

            //  Use assigned package if available, else first available one
            package_id: assigned.package_id || item.packages?.[0]?.id || "",
            environment: assigned.environment || "UAT",
            domain: assigned.domain || "https://test.in",
            request_count: assigned.request_count || 100,
            scope: assigned.scope || "N/A",
            service_status: assigned.status?.value || "inactive",
            adjustment_type:
              assigned.adjustment_type || item.adjustment_type || "",
            assigned_id: assigned.id || null,
            remarks: assigned.remarks || "",
          };
        });

        setAssignedDetails(enriched);
      } else {
        showErrorToast(response?.message || "No data found.");
        setAssignedDetails([]);
      }
    } catch (error) {
      console.error("❌ Error fetching details:", error);
      showErrorToast("Server error while fetching data!");
      setAssignedDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  //  Manual Search
  const handleSearch = () => {
    const { mobile, email, searchKeyword } = searchData;
    const userId = searchText.trim();

    if (!userId && !mobile && !email && !searchKeyword) {
      showErrorToast("Please enter User ID, Mobile, Email, or Search keyword!");
      return;
    }

    fetchAssignedDetails({
      userId: userId.length === 8 ? userId.toUpperCase() : "",
      mobileNumber: mobile,
      email,
      search: searchKeyword,
    });
  };

  useEffect(() => {
    const { searchKeyword } = searchData;

    const timer = setTimeout(() => {
      if (searchKeyword && searchKeyword.length >= 3) {
        handleSearch();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchData.mobile, searchData.email, searchData.searchKeyword]);

  //  Update field in table row
  const updateServiceField = (id, path, value) => {
    setAssignedDetails((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const updated = { ...item };
        const keys = path.split(".");
        let ref = updated;

        keys.forEach((key, index) => {
          if (index === keys.length - 1) {
            ref[key] = value; // set the value at the deepest key
          } else {
            // if intermediate object doesn’t exist, create it
            ref[key] = ref[key] || {};
            ref = ref[key];
          }
        });

        return updated;
      }),
    );
  };

  const handleSave = async () => {
    const userId = searchText.trim().toUpperCase();

    const selected = assignedDetails.filter((item) => item.selected);
    if (selected.length === 0) {
      showErrorToast("Please select at least one service!");
      return;
    }

    try {
      setIsLoading(true);

      // Deep copy selected items to ensure nested objects are read correctly
      const refinedSelected = selected.map((item) => ({
        ...item,
        payoutAccount: { ...item.payoutAccount },
        emandateService: { ...item.emandateService },
      }));

      // Build payload for API
      const payloadForAPI = refinedSelected.map((item) => {
        const payload = {
          end_point: item.end_point,
          service_id: item.service_id || item.id,
          package_id: item.package_id || item.packages?.[0]?.id || "",
          service_status: item.service_status || "inactive",
          scope: item.scope,
          environment: item.environment || "UAT",
          domain: item.domain,
          request_count:
            item.environment === "UAT" ? Number(item.request_count) || 100 : "",
          remarks: item.remarks || "Bulk Assigned via Portal",
        };

        // -------------------------------
        // FUND TRANSFER SETTINGS
        // -------------------------------
        if (item.end_point?.includes("/idfc/fund-transfer")) {
          const pa = item.payoutAccount || {};
          payload.pay_account_status = pa.status?.value || "active";
          payload.daily_limit = pa.daily_limit ?? 0; // default if empty
          payload.monthly_limit = pa.monthly_limit ?? 0;
        }

        // -------------------------------
        // W-EMANDATE SETTINGS
        // -------------------------------
        if (item.end_point?.includes("/w-emandate")) {
          const em = item.emandateService || {};
          payload.login_id = em.login_id || "";
          payload.password = em.password || "";
          payload.merchant_code = em.merchant_code || "";
          payload.scheme_code = em.scheme_code || "";
          payload.encryption_key = em.encryption_key || "";
          payload.encryption_iv = em.encryption_iv || "";
          payload.salt = em.salt || "";
          payload.status = em.status || "inactive";
        }

        console.log("Payload for service:", payload);
        return payload;
      });

      // Call API
      const response = await bulkAssignServices({
        userId,
        selectedServices: payloadForAPI,
      });

      if (response?.status?.code === 200) {
        setResultMessages({
          success: response?.data?.success || [],
          errors: response?.data?.errors || [],
        });
        setShowResultModal(true);

        // Reset selections
        setAssignedDetails((prev) =>
          prev.map((item) => ({ ...item, selected: false })),
        );
      } else {
        showErrorToast(response?.message || "Failed to assign services.");
      }
    } catch (error) {
      console.error("Error in bulk assign:", error);
      showErrorToast("Server error during service assignment!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      {isLoading && <Loader />}
      {showResultModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-4">
            <h2 className="text-lg font-semibold mb-3 text-center text-green-700">
              Service Assignment Result
            </h2>

            {resultMessages.success.length > 0 && (
              <div className="mb-2">
                <h3 className="font-medium text-green-600">✅ Success:</h3>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {resultMessages.success.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            {resultMessages.errors.length > 0 && (
              <div>
                <h3 className="font-medium text-red-600">❌ Errors:</h3>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {resultMessages.errors.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-center mt-4">
              <button
                onClick={() => setShowResultModal(false)}
                className="bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Seo />
      <DivTag className="border-2 border-solid border-blue-800 rounded-xl mt-10 bg-white p-1">
        <ReportHeading name="Activate Bulk Service" />

        <div className="flex justify-center mt-4 mb-3">
          <div className="relative w-1/2 sm:w-1/2 md:w-1/2">
            {/*  Search Input */}
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Enter text to Search"
              className="w-full border border-blue-400 shadow-md h-11 rounded-xl text-gray-800 placeholder-gray-500 outline-none px-4 text-sm focus:ring-2 focus:ring-blue-300 transition-all duration-200"
              maxLength={8}
            />

            {/*  Loader */}
            {isUserLoading && userDropdown.length === 0 && (
              <div className="absolute left-0 right-0 bg-white border border-blue-300 rounded-xl mt-2 py-2 text-center text-gray-500 text-sm shadow-md">
                Searching...
              </div>
            )}

            {/*  Dropdown */}
            {showUserDropdown && (
              <ul
                className="absolute w-full bg-white border border-blue-300 rounded-xl shadow-xl mt-2 z-50 max-h-56 overflow-y-auto animate-fadeIn"
                onScroll={(e) => {
                  const bottom =
                    e.target.scrollHeight - e.target.scrollTop ===
                    e.target.clientHeight;
                  if (bottom && hasMore && !isUserLoading) {
                    handleUserSearch(searchTerm, page + 1, true);
                  }
                }}
              >
                {userDropdown.length > 0
                  ? userDropdown.map((user) => (
                      <li
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className="px-2 py-1 hover:bg-blue-50 cursor-pointer transition-all duration-150 border-b border-gray-100 last:border-none"
                      >
                        <div className="font-semibold text-gray-900 flex justify-between bg-green-100">
                          <span className="text-xs text-blue-600">
                            {user.name ? user.name : "No Name"}
                          </span>
                          <span className="text-xs">{user.user_id}</span>
                        </div>
                      </li>
                    ))
                  : !isUserLoading &&
                    searchTerm.trim() !== "" && (
                      <li className="p-3 text-center text-gray-500 text-sm">
                        No users found
                      </li>
                    )}

                {isUserLoading && userDropdown.length > 0 && (
                  <li className="p-3 text-center text-gray-500 text-sm">
                    Loading more...
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        <span className="text-black hidden md:block text-center">OR</span>

        {/* 🔍 Search Section */}
        <div className="bg-white p-2 mt-2 rounded-xl shadow-md mb-3">
          <div className="flex flex-wrap items-center gap-3 bg-white border border-blue-400 rounded-md p-3 shadow-sm">
            {/* Search Keyword */}
            <input
              type="text"
              placeholder="Search keyword (Service Name)"
              value={searchData.searchKeyword}
              onChange={(e) =>
                setSearchData({ ...searchData, searchKeyword: e.target.value })
              }
              className="border rounded-lg p-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring focus:ring-blue-300"
            />

            <span className="text-black hidden md:block">|</span>

            {/* User ID */}
            <input
              type="text"
              value={searchText.toUpperCase()}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Enter 8-digit User ID..."
              className="border shadow-md h-9 rounded-md text-black outline-none px-2 border-blue-400 flex-1 min-w-[200px]"
              maxLength={8}
            />

            <span className="text-black hidden md:block">|</span>

            {/* Mobile */}
            <input
              type="text"
              placeholder="Mobile Number"
              value={searchData.mobile}
              onChange={(e) =>
                setSearchData({ ...searchData, mobile: e.target.value })
              }
              className="border rounded-lg p-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring focus:ring-blue-300"
            />

            <span className="text-black hidden md:block">|</span>

            {/* Email */}
            <input
              type="email"
              placeholder="Email ID"
              value={searchData.email}
              onChange={(e) =>
                setSearchData({ ...searchData, email: e.target.value })
              }
              className="border rounded-lg p-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring focus:ring-blue-300"
            />

            {/* Search Button */}
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className={`${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white rounded-lg py-2 px-4 text-sm font-semibold w-full md:w-auto`}
            >
              {isLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* ✅ Remarks Input */}

        <div className="flex items-center justify-between">
          <div className="mb-4">
            <label className="mr-2 font-semibold">Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-3 py-2 border-blue-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <span className="ml-3 text-gray-600">
              Showing {filteredAssignedDetails.length} of{" "}
              {assignedDetails.length} records
            </span>
          </div>

          {/* Start (Left) */}
          <div className="flex items-center gap-2 ml-10">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-sm font-medium">Active</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-500 rounded-full"></span>
              <span className="text-sm font-medium">Inactive</span>
            </div>
          </div>

          {/* End (Right) */}
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            } text-white px-3 py-1 rounded-lg`}
          >
            {isLoading ? "Processing..." : "Save Selected"}
          </button>
        </div>

        {/* user details to show when Userid entered */}
        {basicUserData && (
          <div className="w-full flex justify-center ">
            <div className="py-4 bg-green-50 text-green-800 rounded-xl p-4">
              <div className="py-4 flex items-center gap-4 text-2xl font-bold"><User2Icon/> User Details</div>
              <p  className="flex items-center gap-4 md:gap-12 "><span className="font-bold"> Name:</span> {basicUserData?.user?.name}</p>
              <p  className="flex items-center gap-4 md:gap-12 "><span className="font-bold">Company Name: </span>{basicUserData?.user?.company_name || "N/A"}</p>
              <p  className="flex items-center gap-4 md:gap-12 "><span className="font-bold">Mobile:</span> {basicUserData?.user?.mobile_number}</p>
              <p  className="flex items-center gap-4 md:gap-12 "><span className="font-bold">Email:</span> {basicUserData?.user?.email_address}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-3 mt-4">
          {assignedDetails.length > 0 ? (
            <>
              <table className="w-full border border-gray-300 text-sm">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="border px-2 py-2 text-center">SRN</th>
                    <th className="border px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={
                          assignedDetails.length > 0 &&
                          assignedDetails.every((api) => api.selected)
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAssignedDetails((prev) =>
                            prev.map((api) => ({ ...api, selected: checked })),
                          );
                        }}
                      />
                    </th>
                    <th className="border px-2 py-1">API Name</th>
                    <th className="border px-2 py-1">Package</th>
                    <th className="border px-2 py-1">Activation Type</th>
                    <th className="border px-2 py-1">Environment</th>
                    <th className="border px-2 py-1">Domain</th>
                    <th className="border px-2 py-1">Scope</th>
                    <th className="border px-2 py-1">Service Status</th>
                    <th className="border px-2 py-1">Request Count</th>
                    <th className="border px-2 py-1">Remark</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredAssignedDetails.map((api, index) => (
                    <React.Fragment key={api.id || index}>
                      {/* 🌿 Main Row */}
                      <tr
                        className={`${
                          api.service_status === "active"
                            ? "bg-green-200"
                            : api.service_status === "inactive"
                              ? "bg-red-200"
                              : "bg-gray-100"
                        }`}
                      >
                        <td className="border px-2 py-1 text-center">
                          {index + 1}
                        </td>
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={api.selected || false}
                            onChange={() =>
                              setAssignedDetails((prev) =>
                                prev.map((item) =>
                                  item.id === api.id
                                    ? { ...item, selected: !item.selected }
                                    : item,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="border px-2 py-1 font-medium">
                          {api.service_name}
                        </td>

                        {/* Package */}
                        <td className="border px-2 py-1">
                          <select
                            value={api.package_id || ""}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "package_id",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 py-0.5 w-full"
                          >
                            <option value="">Select Package</option>
                            {api.packages?.map((pkg) => (
                              <option key={pkg.id} value={pkg.id}>
                                {pkg.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Activation Type */}
                        <td className="border px-2 py-1">
                          <select
                            value={api.adjustment_type || ""}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "adjustment_type",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 py-0.5 w-full"
                          >
                            <option value="">Select</option>
                            <option value="commission">Commission</option>
                            <option value="charge">Charge</option>
                            <option value="charge_slab">Charge Slab</option>
                          </select>
                        </td>

                        {/* Environment */}
                        <td className="border px-2 py-1">
                          <select
                            value={api.environment}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "environment",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 py-0.5 w-full"
                          >
                            <option value="UAT">UAT</option>
                            <option value="PRODUCTION">PROD</option>
                          </select>
                        </td>

                        {/* Domain */}
                        <td className="border px-2 py-1">
                          <input
                            type="text"
                            value={api.domain}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "domain",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 w-full"
                          />
                        </td>

                        {/* Scope */}
                        <td className="border px-2 py-1">
                          <select
                            value={api.scope || ""}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "scope",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 py-0.5 w-full"
                          >
                            <option value="PORTAL_ACCESS">PORTAL_ACCESS</option>
                            <option value="API_ACCESS">API_ACCESS</option>
                            <option value="BOTH">BOTH</option>
                          </select>
                        </td>

                        {/* Service Status */}
                        <td className="border px-2 py-1">
                          <select
                            value={api.service_status || "inactive"}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "service_status",
                                e.target.value,
                              )
                            }
                            className="border rounded px-1 py-0.5 w-full"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>

                        {/* Request Count */}
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="number"
                            value={api.request_count}
                            disabled={api.environment !== "UAT"}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "request_count",
                                e.target.value,
                              )
                            }
                            className={`border rounded px-1 text-center w-16 ${
                              api.environment !== "UAT"
                                ? "bg-gray-100 cursor-not-allowed"
                                : ""
                            }`}
                          />
                        </td>
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="text"
                            placeholder="Enter remarks..."
                            value={api.remarks}
                            onChange={(e) =>
                              updateServiceField(
                                api.id,
                                "remarks",
                                e.target.value,
                              )
                            }
                            className=" border rounded-md px-2 py-1 w-full"
                          />
                        </td>
                      </tr>

                      {/* FUND TRANSFER SETTINGS */}
                      {api.end_point
                        ?.toLowerCase()
                        .includes("/idfc/fund-transfer") && (
                        <tr className="bg-blue-50">
                          <td colSpan="10" className="p-3">
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                              {/* Status */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Pay Account Status
                                </label>
                                <select
                                  value={
                                    api.payoutAccount?.status?.value ||
                                    "inactive"
                                  }
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "payoutAccount.status.value",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </div>

                              {/* Daily Limit */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Daily Limit
                                </label>
                                <input
                                  type="number"
                                  value={api.payoutAccount?.daily_limit || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "payoutAccount.daily_limit",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Daily Limit"
                                />
                              </div>

                              {/* Monthly Limit */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Monthly Limit
                                </label>
                                <input
                                  type="number"
                                  value={api.payoutAccount?.monthly_limit || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "payoutAccount.monthly_limit",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Monthly Limit"
                                />
                              </div>

                              <div>
                                <label className="block text-xl text-gray-600 mt-5">
                                  Account Number :{" "}
                                  {api.payoutAccount?.account_number || ""}
                                </label>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* W-EMANDATE SETTINGS */}
                      {api.end_point?.toLowerCase().includes("/w-emandate") && (
                        <tr className="bg-blue-50">
                          <td colSpan="10" className="p-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Login ID
                                </label>
                                <input
                                  type="text"
                                  value={api.emandateService?.login_id || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.login_id",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Login ID"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Password
                                </label>
                                <input
                                  type="password"
                                  value={api.emandateService?.password || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.password",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Password"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Merchant Code
                                </label>
                                <input
                                  type="text"
                                  value={
                                    api.emandateService?.merchant_code || ""
                                  }
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.merchant_code",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Merchant Code"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Scheme Code
                                </label>
                                <input
                                  type="text"
                                  value={api.emandateService?.scheme_code || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.scheme_code",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Scheme Code"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Encryption Key
                                </label>
                                <input
                                  type="text"
                                  value={
                                    api.emandateService?.encryption_key || ""
                                  }
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.encryption_key",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Encryption Key"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Encryption IV
                                </label>
                                <input
                                  type="text"
                                  value={
                                    api.emandateService?.encryption_iv || ""
                                  }
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.encryption_iv",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Encryption IV"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Salt
                                </label>
                                <input
                                  type="text"
                                  value={api.emandateService?.salt || ""}
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.salt",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                  placeholder="Salt"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Status
                                </label>
                                <select
                                  value={
                                    api.emandateService?.status || "inactive"
                                  }
                                  onChange={(e) =>
                                    updateServiceField(
                                      api.id,
                                      "emandateService.status",
                                      e.target.value,
                                    )
                                  }
                                  className="border rounded px-2 py-1 w-full"
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`${
                    isLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  } text-white px-3 py-1 rounded-lg`}
                >
                  {isLoading ? "Processing..." : "Save Selected"}
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-center py-6">
              No data found. Please search using filters above.
            </p>
          )}
        </div>
      </DivTag>

      <ToastContainer />
    </motion.div>
  );
};

export default ActivateServiceBulk;
